// src/features/simulators/shared/components/SimulatorsIndex.tsx

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { ManualHelpButton } from '@/features/manual';
import {
  DesktopWindows as KioskIcon,
  Key as DispenserIcon,
  PhoneAndroid as MobileDeskIcon,
  TabletAndroid as MobileKioskIcon,
  Language as OnlineIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { CenteredFormLayout } from '@/components/forms/MyFormLayout';

interface SimulatorCardData {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  route: string;
}

const SIMULATORS: Omit<SimulatorCardData, 'icon'>[] = [
  {
    id: 'kiosk',
    titleKey: 'resources.simulators.index.kiosk.title',
    descriptionKey: 'resources.simulators.index.kiosk.description',
    route: '/simulators/kiosk',
  },
  {
    id: 'dispenser',
    titleKey: 'resources.simulators.index.dispenser.title',
    descriptionKey: 'resources.simulators.index.dispenser.description',
    route: '/simulators/dispenser',
  },
  {
    id: 'mobile-desk',
    titleKey: 'resources.simulators.index.mobile_desk.title',
    descriptionKey: 'resources.simulators.index.mobile_desk.description',
    route: '/simulators/mobile-desk',
  },
  {
    id: 'mobile-kiosk',
    titleKey: 'resources.simulators.index.mobile_kiosk.title',
    descriptionKey: 'resources.simulators.index.mobile_kiosk.description',
    route: '/simulators/mobile-kiosk',
  },
  {
    id: 'online-checkin',
    titleKey: 'resources.simulators.index.online_checkin.title',
    descriptionKey: 'resources.simulators.index.online_checkin.description',
    route: '/simulators/online-checkin',
  },
];

const ICON_MAP: Record<string, React.ReactNode> = {
  kiosk: <KioskIcon />,
  dispenser: <DispenserIcon />,
  'mobile-desk': <MobileDeskIcon />,
  'mobile-kiosk': <MobileKioskIcon />,
  'online-checkin': <OnlineIcon />,
};

export const SimulatorsIndex: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <CenteredFormLayout maxWidth="1200px">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
        {/* Header — the app's own PageTitle recipe (left-aligned h1 + tertiary
            subtitle), same as every list/dashboard screen, instead of a
            centred one-off h4 (F-B-023). */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h1" component="h1" sx={{ color: 'text.primary' }}>
              {translate('resources.simulators.index.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: theme.custom?.text?.tertiary, mt: 0.5 }}>
              {translate('resources.simulators.index.subtitle')}
            </Typography>
          </Box>
          <ManualHelpButton screen="simulators" />
        </Box>

        {/* Simulators Grid */}
        <Grid container spacing={3}>
          {SIMULATORS.map((sim) => (
            <Grid item xs={12} sm={6} md={4} key={sim.id}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => navigate(sim.route)}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                >
                  {ICON_MAP[sim.id]}
                </Box>

                {/* Title */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(sim.titleKey)}
                </Typography>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                  {translate(sim.descriptionKey)}
                </Typography>

                {/* Action */}
                <Button
                  size="small"
                  endIcon={<ArrowIcon />}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  {translate('resources.simulators.index.open_button')}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </CenteredFormLayout>
  );
};
